/* ============================================================================
   GMA Supply Co. — Customizer storefront logic (Tee live + 11-family
   selector, Phase 5 Stage B copy — authored 2026-10-17 in
   ~/workspace/pod/phase5-execution/stage-b/customizer/)
   assets/gma-customizer.js

   STAGE B ADDITIONS vs the Phase 2 original (same architecture, same
   canonical state object, same proof gate, calibrated geometry
   constants UNCHANGED — regionPx front {448,248,300,350} /
   back {448,237,300,350}):
     - 11-family selector driven by the families registry asset
       (boot.familiesUrl → assets/gma-product-families.json, a verbatim
       copy of PRODUCT_FAMILIES_FILLED.json). state.productKey IS the
       selected family. Tee keeps the full existing pipeline; every
       other family renders its registry state (COMING SOON /
       NOT AVAILABLE / shop-product) with its reason and is fail-closed:
       no variant resolution, no price, no proof request, Add to Cart
       disabled with the state as its label. If the registry asset
       fails to load, non-tee families render as unverified/unavailable
       — never as purchasable.
     - Retired-artwork blocklist: the trade registry asset
       (boot.tradeRegistryUrl → assets/gma-trade-registry.json) names
       the retired design ID; matching designs are filtered out of the
       library and refused by selectDesign.
     - Draft persistence now also records the selected family.

   Implements Custom Studio Spec v1.0:
     §6  One SVG stage, one coordinate system. No screenshot hotspots,
         no fixed pixel offsets — all geometry derives from the calibrated
         template constants in GMA_CONFIG.templates below.
     §7  ONE canonical state object (`state`). Every control, rail, price
         and the renderer read from it. Update order on every change:
           input -> validate compatibility -> resolve Shopify variant
           -> load correct mockup -> recalibrate art -> recalc
           price/availability -> update all UI selections -> validate proof
           -> enable/disable Add to Cart.
     §8  Add to Cart goes through Shopify's Ajax cart/add.js with the real
         variant id and line-item properties (Design, _design_id,
         _design_version, _proof_id, _placement_json). The button stays
         disabled until the selection is valid AND a server-side proof
         exists (proof bridge, see proof-bridge/).

   FILL-IN POINTS (see BUILD_NOTES.md for the full table):
     - GMA_CONFIG.product.fallbackVariants — only used until the section's
       product setting is assigned in the theme editor; the live Shopify
       variant JSON (emitted by the section) then becomes the authority.
     - GMA_CONFIG.templates geometry — DERIVED values; must be replaced by
       measured values per templates/CALIBRATION.md before go-live.
     - Boot JSON (emitted by sections/gma-customizer.liquid): proof service
       URL, design image URLs, mockup URLs. Empty values degrade honestly:
       unmapped variant -> "Unavailable until priced"; missing mockup ->
       preview error; missing proof service -> cart stays disabled.
   ============================================================================ */
(function () {
  'use strict';

  /* ================= CONFIG ================= */
  var GMA_CONFIG = {
    pageSize: 12,
    nudgeStepNormalized: 0.02,   // one nudge = 2% of the print region
    scaleStep: 1.12,              // Smaller/Larger multiplier
    minScale: 0.2,
    zoomStep: 0.5,
    zoomMax: 3,
    proofDebounceMs: 500,

    product: {
      productKey: 'tee',
      name: 'Custom Tee',
      model: 'Gildan 5000',
      printMethod: 'dtg',
      compatibleType: 'tee',
      colors: ['Black', 'White', 'Navy'],
      sizes: ['S', 'M', 'L', 'XL', '2XL'],
      views: ['front', 'back'],
      /* Verified 2026-10-17 by the parallel Shopify setup task (admin):
         product "GMA Custom Tee", id 10806594306342, Active/unlisted,
         inventory untracked (so variants are purchasable unless manually
         disabled — available: true). All variants $18.49 = 1849 cents.
         This table is the config-of-record AND the fallback: when the
         section's product setting is assigned in the theme editor, the
         section emits the live Shopify variant JSON and that becomes the
         authority (buildVariantMap). Both sources must describe the same
         product; if they ever disagree, the live Shopify data wins. */
      shopifyProductId: 10806594306342,
      fallbackVariants: {
        'Black|S':   { variantId: 54131803914534, priceCents: 1849, available: true },
        'Black|M':   { variantId: 54131803947302, priceCents: 1849, available: true },
        'Black|L':   { variantId: 54131803980070, priceCents: 1849, available: true },
        'Black|XL':  { variantId: 54131804012838, priceCents: 1849, available: true },
        'Black|2XL': { variantId: 54131804045606, priceCents: 1849, available: true },
        'White|S':   { variantId: 54131804078374, priceCents: 1849, available: true },
        'White|M':   { variantId: 54131804111142, priceCents: 1849, available: true },
        'White|L':   { variantId: 54131804143910, priceCents: 1849, available: true },
        'White|XL':  { variantId: 54131804176678, priceCents: 1849, available: true },
        'White|2XL': { variantId: 54131804209446, priceCents: 1849, available: true },
        'Navy|S':    { variantId: 54131803750694, priceCents: 1849, available: true },
        'Navy|M':    { variantId: 54131803783462, priceCents: 1849, available: true },
        'Navy|L':    { variantId: 54131803816230, priceCents: 1849, available: true },
        'Navy|XL':   { variantId: 54131803848998, priceCents: 1849, available: true },
        'Navy|2XL':  { variantId: 54131803881766, priceCents: 1849, available: true }
      }
    },

    /* Calibrated template geometry — mirrors
       templates/tee-gildan5000.calibration.json. STATUS: DERIVED, NOT
       VERIFIED. Replace with measured values per CALIBRATION.md and keep
       the two files in sync. Stage = the mockup's own coordinate system. */
    templates: {
      stage: { w: 1200, h: 1200 },
      views: {
        front: {
          regionId: 'front-chest',
          regionIn: { w: 12, h: 14 },
          regionPx: { x: 448, y: 248, w: 300, h: 350 }
        },
        back: {
          regionId: 'back-print',
          regionIn: { w: 12, h: 14 },
          regionPx: { x: 448, y: 237, w: 300, h: 350 }
        }
      },
      /* Agreed theme-asset filenames (BUILD_NOTES.md). The section turns
         these into real asset URLs in the boot JSON. */
      mockupFiles: {
        front: { Black: 'gma-tee-front-black.png', White: 'gma-tee-front-white.png', Navy: 'gma-tee-front-navy.png' },
        back:  { Black: 'gma-tee-back-black.png',  White: 'gma-tee-back-white.png',  Navy: 'gma-tee-back-navy.png' }
      },
      shadingFiles: { front: 'gma-tee-front-shading.png', back: 'gma-tee-back-shading.png' }
    }
  };

  /* ================= helpers ================= */
  function $(root, sel) { return root.querySelector(sel); }
  function $$(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }
  function clamp(v, lo, hi) { return Math.min(hi, Math.max(lo, v)); }
  function titleCase(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  /* ================= per-instance boot ================= */
  function init(root) {
    var boot = {};
    try {
      var bootEl = root.querySelector('script[data-gma-boot]');
      boot = JSON.parse(bootEl.textContent);
    } catch (e) { /* boot stays empty; UI degrades to honest disabled states */ }

    var els = {
      summary: root.querySelector('[data-gma-summary]'),
      search: root.querySelector('[data-gma-search]'),
      chips: root.querySelector('[data-gma-chips]'),
      grid: root.querySelector('[data-gma-grid]'),
      empty: root.querySelector('[data-gma-empty]'),
      loadMore: root.querySelector('[data-gma-load-more]'),
      libraryToggle: root.querySelector('[data-gma-library-toggle]'),
      libraryBody: root.querySelector('[data-gma-library-body]'),
      svg: root.querySelector('[data-gma-stage]'),
      stageZoom: root.querySelector('[data-gma-stage-zoom]'),
      baseImg: root.querySelector('[data-gma-base]'),
      artImg: root.querySelector('[data-gma-art]'),
      shadingImg: root.querySelector('[data-gma-shading]'),
      regionRect: root.querySelector('[data-gma-region]'),
      loader: root.querySelector('[data-gma-loader]'),
      stageMsg: root.querySelector('[data-gma-stage-msg]'),
      printSize: root.querySelector('[data-gma-print-size]'),
      clearDesign: root.querySelector('[data-gma-clear-design]'),
      colors: root.querySelector('[data-gma-colors]'),
      sizes: root.querySelector('[data-gma-sizes]'),
      colorName: root.querySelector('[data-gma-color-name]'),
      sizeName: root.querySelector('[data-gma-size-name]'),
      price: root.querySelector('[data-gma-price]'),
      availability: root.querySelector('[data-gma-availability]'),
      status: root.querySelector('[data-gma-status]'),
      proofLine: root.querySelector('[data-gma-proof-line]'),
      turnstile: root.querySelector('[data-gma-turnstile]'),
      atc: root.querySelector('[data-gma-atc]'),
      rail: root.querySelector('[data-gma-rail]'),
      productCard: root.querySelector('[data-gma-product-card]'),
      families: root.querySelector('[data-gma-families]'),
      familyNotice: root.querySelector('[data-gma-family-notice]'),
      familyNoticeName: root.querySelector('[data-gma-family-notice-name]'),
      familyNoticeBadge: root.querySelector('[data-gma-family-notice-badge]'),
      familyNoticeReason: root.querySelector('[data-gma-family-notice-reason]'),
      teeControls: root.querySelector('[data-gma-tee-controls]')
    };

    /* ---- variant authority: live Shopify product JSON when the section
       has a product assigned; otherwise the marked fallback table. ---- */
    var variantMap = buildVariantMap(boot);

    /* ---- canonical state (spec §7). Placements are stored per view so
       switching Front/Back never loses work; `placement` in the spec's
       example shape is placements[state.view]. ---- */
    var state = {
      designId: null,
      designVersion: null,
      productKey: GMA_CONFIG.product.productKey,
      shopifyProductId: boot.shopifyProductId || GMA_CONFIG.product.shopifyProductId || null,
      variantId: null,
      size: 'L',
      color: 'Black',
      view: 'front',
      placements: { front: null, back: null },
      quantity: 1,
      unitPriceCents: null,
      availability: 'unmapped',
      proofStatus: 'idle',   // idle | pending | valid | error | stale
      proofId: null
    };

    /* ---- transient runtime (deliberately NOT part of state) ---- */
    var runtime = {
      designs: [],
      designsById: {},
      filterTag: 'all',
      filterQuery: '',
      visibleCount: GMA_CONFIG.pageSize,
      token: 0,               // staleness guard for async stage loads
      proofTimer: null,
      proofSeq: 0,
      zoom: 1,
      mockupLoading: false,
      mockupError: false,
      artBroken: false,
      adding: false,
      loadedMockups: {},      // "view|color" -> true once the base image loaded
      families: [],           // registry rows from the families asset
      familiesByKey: {},      // family_key -> registry row
      familiesLoaded: false,  // false until the registry asset resolves
      blockedDesignIds: [],   // retired design IDs from the trade registry
      draftFamily: null       // family restored from a saved draft, if any
    };

    var debug = /[?&]gma_debug=1/.test(window.location.search) || boot.showRegionOutline === true;

    /* ================= variant map ================= */
    function buildVariantMap(bootData) {
      var map = {};
      if (bootData && Array.isArray(bootData.variants) && bootData.variants.length) {
        var opts = bootData.productOptions || [];
        var ci = 0, si = 1;
        opts.forEach(function (name, i) {
          if (/color|colour/i.test(name)) ci = i;
          if (/size/i.test(name)) si = i;
        });
        bootData.variants.forEach(function (v) {
          var color = v['option' + (ci + 1)];
          var size = v['option' + (si + 1)];
          if (!color || !size) return;
          map[color + '|' + size] = {
            variantId: v.id,
            priceCents: (typeof v.price === 'number') ? v.price : parseInt(v.price, 10),
            available: v.available !== false
          };
        });
        return map;
      }
      return GMA_CONFIG.product.fallbackVariants;
    }

    function variantEntry(color, size) {
      return variantMap[color + '|' + size] || null;
    }

    /* ================= product families (Phase 5 Stage B) =================
       The families registry (PRODUCT_FAMILIES_FILLED.json, served as the
       theme asset named in boot.familiesUrl) is the ONLY source of family
       eligibility. Badges/reasons below are plain-English renderings of
       the registry's own approval_state / retail_price_status fields —
       no family is ever presented as purchasable from config alone. */

    var GMA_FAMILY_FALLBACK = [
      { family_key: 'tee', blank: 'Gildan 5000' },
      { family_key: 'hoodie', blank: 'Gildan 18500' },
      { family_key: 'crewneck', blank: 'Gildan 18000' },
      { family_key: 'hat', blank: 'OTTO 39-165 Foam Trucker' },
      { family_key: 'beanie', blank: 'Atlantis Ribbed Knit' },
      { family_key: 'mug', blank: 'Ceramic mug' },
      { family_key: 'sticker', blank: 'Kiss-cut sticker' },
      { family_key: 'poster', blank: 'Poster' },
      { family_key: 'duffel', blank: 'Duffel bag' },
      { family_key: 'phone_case', blank: 'Phone case' },
      { family_key: 'magnet', blank: 'Magnet' }
    ];

    function familyDisplayName(fam) {
      var names = {
        tee: 'Custom Tee', hoodie: 'Hoodie', crewneck: 'Crewneck',
        hat: 'Trucker Hat', beanie: 'Beanie', mug: 'Mug', sticker: 'Sticker',
        poster: 'Poster', duffel: 'Duffel Bag', phone_case: 'Phone Case',
        magnet: 'Magnet'
      };
      return names[fam.family_key] || titleCase(String(fam.family_key).replace(/_/g, ' '));
    }

    /* Map a registry row to its customer-facing state. Tee is the only
       family the Studio can sell (and even it stays proof-gated). */
    function familyStatusFor(fam) {
      if (!fam) {
        return { mode: 'locked', badge: 'Not available', reason: 'This product is not available yet.' };
      }
      if (fam.family_key === 'tee') {
        return { mode: 'live', badge: 'Available', reason: '' };
      }
      if (!runtime.familiesLoaded) {
        return { mode: 'locked', badge: 'Not available',
          reason: 'We could not load this product\'s availability — please refresh the page.' };
      }
      /* audit #28 (2026-10-29): these products ARE sold in the shop as ready-made designs, so the Studio must
         not call them "Not available" / "Coming soon". Custom printing for them is still Studio-locked. */
      var READY_MADE = { hoodie: 'hoodies', crewneck: 'crewnecks', mug: 'mugs', poster: 'posters', phone_case: 'phone cases', magnet: 'magnets' };
      if (READY_MADE[fam.family_key]) {
        return { mode: 'locked', badge: 'In the shop',
          reason: 'Custom ' + READY_MADE[fam.family_key] + ' are not in the Studio yet. Our ready-made ' + READY_MADE[fam.family_key] + ' are in the shop now.' };
      }
      if (fam.family_key === 'sticker') {
        /* LIVE as fixed shop SKUs (registry approval_state
           CURRENT_4_99_NO_PRICE_CHANGE) — but not a Studio product. */
        return { mode: 'locked', badge: 'Shop product',
          reason: 'Stickers are sold ready-made in the shop with fixed designs. Custom sticker printing is not offered in the Studio yet.' };
      }
      var stateText = String((fam.approval_state || '') + ' ' + (fam.retail_price_status || ''));
      if (/LOCK_EMBROIDERY/.test(stateText)) {
        return { mode: 'locked', badge: 'Not available',
          reason: 'Embroidery is coming soon — stitched designs are not ready yet.' };
      }
      if (/RETAIL_4_00_APPROVED/.test(stateText)) {
        return { mode: 'locked', badge: 'Not available',
          reason: 'Magnets are sold ready-made in the shop with fixed designs. Custom magnets are not offered in the Studio yet.' };
      }
      if (/SWAP_NOT_APPROVED|BLANK_SWAP/.test(stateText)) {
        var swapReason = 'This product is coming soon to the Studio.';
        if (fam.family_key === 'hat') {
          swapReason += ' Embroidery is not ready yet.';
        }
        return { mode: 'locked', badge: 'Not available', reason: swapReason };
      }
      if (/NOT_CREATED/.test(stateText)) {
        return { mode: 'locked', badge: 'Coming soon',
          reason: 'Coming soon — this product is not in the Studio yet.' };
      }
      return { mode: 'locked', badge: 'Not available', reason: 'This product is not available yet.' };
    }

    function familyIsLive() {
      return state.productKey === 'tee';
    }

    function loadFamilies() {
      function useRows(rows, loaded) {
        runtime.families = rows;
        runtime.familiesByKey = {};
        rows.forEach(function (f) { runtime.familiesByKey[f.family_key] = f; });
        runtime.familiesLoaded = loaded;
        renderFamilies();
        /* a draft saved on a locked family restores to that family's
           locked view (never to a purchasable state) */
        if (runtime.draftFamily && runtime.draftFamily !== state.productKey &&
            runtime.familiesByKey[runtime.draftFamily]) {
          selectFamily(runtime.draftFamily);
        } else if (!familyIsLive()) {
          renderLockedFamily();
        }
      }
      if (!boot.familiesUrl) { useRows(GMA_FAMILY_FALLBACK, false); return; }
      fetch(boot.familiesUrl, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('families ' + r.status); return r.json(); })
        .then(function (data) {
          var rows = (data && data.families) || [];
          if (!rows.length) throw new Error('families empty');
          useRows(rows, true);
        })
        .catch(function () { useRows(GMA_FAMILY_FALLBACK, false); });
    }

    function renderFamilies() {
      if (!els.families) return;
      els.families.innerHTML = '';
      runtime.families.forEach(function (fam) {
        var st = familyStatusFor(fam);
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'gma-cz__family' +
          (fam.family_key === state.productKey ? ' is-active' : '') +
          (st.mode === 'locked' ? ' is-locked' : '');
        b.dataset.familyKey = fam.family_key;
        b.setAttribute('aria-pressed', fam.family_key === state.productKey ? 'true' : 'false');
        var name = document.createElement('span');
        name.className = 'gma-cz__family-name';
        name.textContent = familyDisplayName(fam);
        var sub = document.createElement('span');
        sub.className = 'gma-cz__family-state';
        sub.textContent = st.mode === 'live' ? (fam.blank || '') : st.badge;
        b.appendChild(name);
        b.appendChild(sub);
        b.setAttribute('aria-label', familyDisplayName(fam) +
          (st.mode === 'live' ? ' — available' : ' — ' + st.badge + '. ' + st.reason));
        b.addEventListener('click', function () { selectFamily(fam.family_key); });
        els.families.appendChild(b);
      });
    }

    function selectFamily(key) {
      if (key !== 'tee' && !runtime.familiesByKey[key]) return;  // unknown family: ignore
      if (state.productKey === key) { renderFamilies(); return; }
      state.productKey = key;
      /* a family switch invalidates any tee proof/variant resolution */
      state.variantId = null;
      state.unitPriceCents = null;
      state.proofStatus = 'idle';
      state.proofId = null;
      if (runtime.proofTimer) clearTimeout(runtime.proofTimer);
      renderFamilies();
      applyChange('family');
    }

    /* Locked-family render: the options panel swaps to the registry
       notice, the stage shows NO garment (there is no verified blank
       mockup for an unqualified family — showing the tee would be a
       fake preview), price/availability show the state, and the proof
       line stays empty because no proof is ever requested. */
    function renderLockedFamily() {
      var fam = runtime.familiesByKey[state.productKey];
      var st = familyStatusFor(fam);
      var label = fam ? familyDisplayName(fam) : titleCase(state.productKey.replace(/_/g, ' '));

      if (els.teeControls) els.teeControls.hidden = true;
      if (els.familyNotice) els.familyNotice.hidden = false;
      if (els.familyNoticeName) els.familyNoticeName.textContent = label + (fam && fam.blank ? ' — ' + fam.blank : '');
      if (els.familyNoticeBadge) els.familyNoticeBadge.textContent = st.badge;
      if (els.familyNoticeReason) els.familyNoticeReason.textContent = st.reason;

      if (els.baseImg) els.baseImg.style.display = 'none';
      if (els.artImg) els.artImg.style.display = 'none';
      if (els.shadingImg) els.shadingImg.style.display = 'none';
      if (els.regionRect) els.regionRect.hidden = true;
      if (els.loader) els.loader.hidden = true;
      if (els.stageMsg) {
        els.stageMsg.textContent = label + ' preview is not available yet — there is no verified blank mockup for this product.';
      }
      if (els.printSize) els.printSize.textContent = '';
      if (els.price) els.price.textContent = '—';
      if (els.availability) els.availability.textContent = st.badge;
      if (els.proofLine) els.proofLine.textContent = '';
      if (els.rail && els.rail.parentElement) els.rail.parentElement.hidden = true;

      if (els.summary) {
        var design = currentDesign();
        els.summary.textContent = design
          ? 'Design: ' + design.title + ' · ' + label + ' — ' + st.badge
          : 'Pick a design to start.';
      }
      renderGridSelection();
    }

    /* ================= trade registry — retired-artwork blocklist ======
       The trade registry names the retired design ID
       (retired_artwork_detail.design_id). Designs on the blocklist are
       filtered out of the library and refused by selectDesign. If the
       registry asset cannot be loaded, the library still loads from the
       design manifest, which contains approved designs only (verified
       at Phase 1 import); the deploy checklist requires both registry
       assets to be present before launch QA. */
    function loadTradeRegistry() {
      if (!boot.tradeRegistryUrl) return Promise.resolve();
      return fetch(boot.tradeRegistryUrl, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('registry ' + r.status); return r.json(); })
        .then(function (data) {
          var retired = data && data.retired_artwork_detail;
          if (retired && retired.design_id) runtime.blockedDesignIds.push(retired.design_id);
        })
        .catch(function () { /* see note above — manifest is approved-only data */ });
    }

    function designBlocked(design) {
      return runtime.blockedDesignIds.indexOf(design.design_id) !== -1;
    }

    /* ================= design manifest ================= */
    function designImageUrl(design) {
      if (!design) return '';
      if (boot.designUrls && boot.designUrls[design.file]) return boot.designUrls[design.file];
      var u = design.preview_rgba_url || design.thumbnail_url;
      if (u && /^https?:\/\//.test(u)) return u;
      return '';
    }

    function loadManifest() {
      if (!boot.manifestUrl) { onManifestError('The design library could not be loaded — please refresh the page.'); return; }
      fetch(boot.manifestUrl, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('manifest ' + r.status); return r.json(); })
        .then(function (data) {
          runtime.designs = (data.designs || []).filter(function (d) {
            return d.status === 'approved' &&
              !designBlocked(d) &&
              (d.compatible_product_types || []).indexOf(GMA_CONFIG.product.compatibleType) !== -1;
          });
          runtime.designs.forEach(function (d) { runtime.designsById[d.design_id] = d; });
          buildChips();
          restoreDraft();
          /* if the families registry already resolved, a draft saved on
             a locked family restores here; if it resolves later,
             loadFamilies performs the same restore (both orders covered) */
          if (runtime.draftFamily && runtime.familiesByKey[runtime.draftFamily]) {
            selectFamily(runtime.draftFamily);
          }
          renderGrid();
          applyChange('init');
        })
        .catch(function () { onManifestError('Design library failed to load. Please refresh the page.'); });
    }

    function onManifestError(msg) {
      if (els.empty) { els.empty.hidden = false; els.empty.textContent = msg; }
      setStatus(msg, 'error');
      applyChange('manifest-error');
    }

    /* ================= draft persistence (spec §7) =================
       Draft config only — never prices or variant ids (re-resolved live). */
    var DRAFT_KEY = 'gma-cz-draft-tee';
    function saveDraft() {
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify({
          designId: state.designId, productKey: state.productKey,
          color: state.color, size: state.size,
          view: state.view, placements: state.placements
        }));
      } catch (e) { /* private mode etc. — draft persistence is optional */ }
    }
    /* Draft restore — family/color/size/view only. Owner rule (2026-10-18):
       the Studio ALWAYS opens on the plain blank garment. A saved draft
       must never put a design on the shirt before the visitor picks one
       in this visit, so the draft's designId and placements are
       deliberately NOT restored here (saveDraft still records them; they
       are ignored). Designs are only ever applied by an explicit click. */
    function restoreDraft() {
      try {
        var raw = sessionStorage.getItem(DRAFT_KEY);
        if (!raw) return;
        var d = JSON.parse(raw);
        if (d.productKey && d.productKey !== 'tee') runtime.draftFamily = d.productKey;
        if (GMA_CONFIG.product.colors.indexOf(d.color) !== -1) state.color = d.color;
        if (GMA_CONFIG.product.sizes.indexOf(d.size) !== -1) state.size = d.size;
        if (GMA_CONFIG.product.views.indexOf(d.view) !== -1) state.view = d.view;
      } catch (e) { /* ignore corrupt draft */ }
    }

    /* ================= selection / geometry ================= */
    function currentDesign() { return state.designId ? runtime.designsById[state.designId] : null; }

    function defaultPlacement() {
      return { xNormalized: 0.5, yNormalized: 0.5, scale: 1, rotationDegrees: 0 };
    }

    /* Fit a design inside the region at its resolution cap (spec §5:
       max print width = px/300). Returns {w, h} in inches at scale = 1. */
    function designFit(design, regionIn) {
      var aspect = design.pixel_width / design.pixel_height;
      var w = Math.min(regionIn.w, design.max_print_width_in);
      var h = w / aspect;
      var maxH = Math.min(regionIn.h, design.max_print_height_in);
      if (h > maxH) { h = maxH; w = h * aspect; }
      return { w: w, h: h };
    }

    function maxScale(design, regionIn) {
      var fit = designFit(design, regionIn);
      return Math.min(regionIn.w / fit.w, regionIn.h / fit.h);
    }

    /* Clamp a placement so the artwork can never leave the print region or
       exceed its resolution cap (spec §6 placement controls). */
    function clampPlacement(p, design, regionIn) {
      var fit = designFit(design, regionIn);
      p.scale = clamp(p.scale, GMA_CONFIG.minScale, maxScale(design, regionIn));
      var wIn = fit.w * p.scale, hIn = fit.h * p.scale;
      var halfW = (wIn / regionIn.w) / 2, halfH = (hIn / regionIn.h) / 2;
      p.xNormalized = clamp(p.xNormalized, halfW, 1 - halfW);
      p.yNormalized = clamp(p.yNormalized, halfH, 1 - halfH);
      return p;
    }

    function stageGeometry() {
      var design = currentDesign();
      var p = state.placements[state.view];
      if (!design || !p) return null;
      var tpl = GMA_CONFIG.templates.views[state.view];
      var region = tpl.regionPx, regionIn = tpl.regionIn;
      var fit = designFit(design, regionIn);
      var wIn = fit.w * p.scale, hIn = fit.h * p.scale;
      var wPx = (wIn / regionIn.w) * region.w;
      var hPx = (hIn / regionIn.h) * region.h;
      var cx = region.x + p.xNormalized * region.w;
      var cy = region.y + p.yNormalized * region.h;
      return { x: cx - wPx / 2, y: cy - hPx / 2, w: wPx, h: hPx, cx: cx, cy: cy, wIn: wIn, hIn: hIn };
    }

    /* ================= the update pipeline (spec §7 order) ================= */
    function applyChange(reason) {
      /* Stage B fail-closed branch: a locked family NEVER enters the tee
         pipeline — no variant resolution, no mockup load, no proof
         request, no purchasable state. It renders its registry notice. */
      if (!familyIsLive()) {
        renderLockedFamily();
        updateAtc();
        saveDraft();
        return;
      }
      /* returning to the tee restores the tee controls + color rail */
      if (els.teeControls) els.teeControls.hidden = false;
      if (els.familyNotice) els.familyNotice.hidden = true;

      /* 1 — validate compatibility */
      var design = currentDesign();
      if (design && state.placements[state.view] == null) {
        state.placements[state.view] = defaultPlacement();
      }
      if (!design) { state.placements = { front: null, back: null }; }

      /* 2 — resolve Shopify variant */
      resolveVariant();

      /* 3 — load correct mockup, then continue (token-guarded) */
      var token = ++runtime.token;
      ensureMockup(state.view, state.color).then(function () {
        if (token !== runtime.token) return;  // a newer change superseded us
        /* 4 — recalibrate art */
        if (design && state.placements[state.view]) {
          clampPlacement(state.placements[state.view], design,
            GMA_CONFIG.templates.views[state.view].regionIn);
        }
        /* 5+6 — recalc price/availability + update every UI selection */
        renderAll();
        /* 7 — validate proof (debounced) */
        scheduleProof();
        /* 8 — Add to Cart gate */
        updateAtc();
        saveDraft();
      });
    }

    function resolveVariant() {
      if (!familyIsLive()) {
        state.variantId = null; state.unitPriceCents = null; state.availability = 'family_locked';
        return;
      }
      var entry = variantEntry(state.color, state.size);
      if (!entry) {
        state.variantId = null; state.unitPriceCents = null; state.availability = 'unmapped';
      } else if (entry.variantId == null) {
        state.variantId = null; state.unitPriceCents = entry.priceCents; state.availability = 'unpriced';
      } else if (entry.available === false) {
        state.variantId = entry.variantId; state.unitPriceCents = entry.priceCents; state.availability = 'sold_out';
      } else {
        state.variantId = entry.variantId; state.unitPriceCents = entry.priceCents; state.availability = 'available';
      }
    }

    function mockupUrl(view, color) {
      var files = GMA_CONFIG.templates.mockupFiles[view] || {};
      var file = files[color];
      if (!file) return '';
      if (boot.mockups && boot.mockups[view] && boot.mockups[view][color]) return boot.mockups[view][color];
      return '';
    }

    function ensureMockup(view, color) {
      var key = view + '|' + color;
      var url = mockupUrl(view, color);
      runtime.mockupError = false;
      if (!url) {
        runtime.mockupLoading = false;
        runtime.mockupError = true;
        if (els.loader) els.loader.hidden = true;
        return Promise.resolve();
      }
      if (runtime.loadedMockups[key]) {
        runtime.mockupLoading = false;
        if (els.loader) els.loader.hidden = true;
        return Promise.resolve();
      }
      runtime.mockupLoading = true;
      if (els.loader) els.loader.hidden = false;
      updateAtc();
      return new Promise(function (resolve) {
        var img = new Image();
        img.onload = function () {
          runtime.loadedMockups[key] = true;
          runtime.mockupLoading = false;
          if (els.loader) els.loader.hidden = true;
          resolve();
        };
        img.onerror = function () {
          runtime.mockupLoading = false;
          runtime.mockupError = true;
          if (els.loader) els.loader.hidden = true;
          resolve();
        };
        img.src = url;
      });
    }

    /* ================= rendering — everything reads `state` ================= */
    function renderAll() {
      renderSummary();
      renderClearDesign();
      renderGridSelection();
      renderOptionButtons();
      renderStage();
      renderPrice();
      renderRail();
    }

    function renderSummary() {
      if (!els.summary) return;
      var design = currentDesign();
      els.summary.textContent = design
        ? 'Design: ' + design.title + ' · Tee (' + state.color + ' / ' + state.size + ') · ' + titleCase(state.view) + ' view'
        : 'Pick a design to start.';
    }

    /* Clear-design control: only offered while a design is on the shirt. */
    function renderClearDesign() {
      if (els.clearDesign) els.clearDesign.hidden = !state.designId;
    }

    function buildChips() {
      if (!els.chips) return;
      var tags = ['all'];
      runtime.designs.forEach(function (d) {
        (d.trade_tags || []).forEach(function (t) { if (tags.indexOf(t) === -1) tags.push(t); });
      });
      els.chips.innerHTML = '';
      tags.forEach(function (tag) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'gma-cz__chip' + (runtime.filterTag === tag ? ' is-active' : '');
        b.textContent = tag === 'all' ? 'All Designs' : (tag === 'hvac' ? 'HVAC' : titleCase(tag));
        b.setAttribute('aria-pressed', runtime.filterTag === tag ? 'true' : 'false');
        b.addEventListener('click', function () {
          runtime.filterTag = tag;
          runtime.visibleCount = GMA_CONFIG.pageSize;
          buildChips(); renderGrid();
        });
        els.chips.appendChild(b);
      });
    }

    function filteredDesigns() {
      var q = runtime.filterQuery.trim().toLowerCase();
      return runtime.designs.filter(function (d) {
        if (runtime.filterTag !== 'all' && (d.trade_tags || []).indexOf(runtime.filterTag) === -1) return false;
        if (q) {
          var hay = (d.title + ' ' + (d.trade_tags || []).join(' ')).toLowerCase();
          if (hay.indexOf(q) === -1) return false;
        }
        return true;
      });
    }

    function renderGrid() {
      if (!els.grid) return;
      var list = filteredDesigns();
      var shown = list.slice(0, runtime.visibleCount);
      els.grid.innerHTML = '';
      shown.forEach(function (d) {
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'gma-cz__card' + (d.design_id === state.designId ? ' is-active' : '');
        card.dataset.designId = d.design_id;
        card.setAttribute('aria-pressed', d.design_id === state.designId ? 'true' : 'false');
        card.setAttribute('aria-label', 'Use design ' + d.title);

        var img = document.createElement('img');
        img.alt = d.title;
        img.loading = 'lazy';
        var url = designImageUrl(d);
        if (url) {
          img.src = url;
        } else {
          card.classList.add('is-broken');
        }
        img.addEventListener('error', function () {
          card.classList.add('is-broken');
          var lbl = document.createElement('span');
          lbl.className = 'gma-cz__card-broken-label';
          lbl.textContent = 'Artwork unavailable';
          card.appendChild(lbl);
          if (d.design_id === state.designId) {
            runtime.artBroken = true;
            if (els.stageMsg) els.stageMsg.textContent = 'Artwork unavailable — this design cannot be ordered right now.';
            updateAtc();
          }
        });

        var check = document.createElement('span');
        check.className = 'gma-cz__card-check';
        check.textContent = '✓';
        check.setAttribute('aria-hidden', 'true');

        var name = document.createElement('span');
        name.className = 'gma-cz__card-name';
        name.textContent = d.title;

        card.appendChild(img);
        card.appendChild(check);
        card.appendChild(name);
        card.addEventListener('click', function () { selectDesign(d.design_id, false); });
        els.grid.appendChild(card);
      });
      if (els.empty) {
        els.empty.hidden = list.length !== 0;
        if (list.length === 0 && runtime.designs.length > 0) {
          els.empty.textContent = 'No designs match your search. Try a different word or trade.';
        }
      }
      if (els.loadMore) els.loadMore.hidden = list.length <= runtime.visibleCount;
    }

    function renderGridSelection() {
      if (!els.grid) return;
      $$ (els.grid, '.gma-cz__card').forEach(function (card) {
        var active = card.dataset.designId === state.designId;
        card.classList.toggle('is-active', active);
        card.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }

    function selectDesign(designId, silent) {
      var d = runtime.designsById[designId];
      if (!d) return;
      if (designBlocked(d)) {
        /* retired artwork — refused everywhere, named in the trade registry */
        setStatus('That design has been retired and cannot be ordered.', 'error');
        return;
      }
      state.designId = d.design_id;
      state.designVersion = d.version;
      runtime.artBroken = false;
      state.proofStatus = 'idle';
      state.proofId = null;
      if (!silent) applyChange('design');
    }

    /* Remove the chosen design entirely (owner order 2026-10-18 — the
       placement Reset tool only resets size/position; there was no way to
       take a design back off the shirt). Returns the studio to the exact
       no-design state: blank garment, placements cleared (applyChange also
       re-clears them for a null design), proof discarded, status line
       emptied, Add to Cart back to "Pick a design first" (disabled) and
       the summary back to "Pick a design to start." — all re-rendered
       from state by the same applyChange pipeline as every other change,
       which also rewrites the saved draft with designId null. The proof
       gate itself is untouched: clearing can only ever disable ordering. */
    function clearDesign() {
      if (!state.designId) return;
      state.designId = null;
      state.designVersion = null;
      state.placements = { front: null, back: null };
      runtime.artBroken = false;
      state.proofStatus = 'idle';
      state.proofId = null;
      if (runtime.proofTimer) { clearTimeout(runtime.proofTimer); runtime.proofTimer = null; }
      setStatus('');
      applyChange('clear-design');
    }

    function renderOptionButtons() {
      /* colors */
      if (els.colors) {
        els.colors.innerHTML = '';
        GMA_CONFIG.product.colors.forEach(function (color) {
          var anyAvailable = GMA_CONFIG.product.sizes.some(function (size) {
            var e = variantEntry(color, size);
            return e && e.variantId != null && e.available !== false;
          });
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'gma-cz__swatch' + (color === state.color ? ' is-active' : '');
          b.dataset.color = color;
          b.disabled = !anyAvailable;
          b.setAttribute('aria-pressed', color === state.color ? 'true' : 'false');
          b.setAttribute('aria-label', 'Color ' + color + (anyAvailable ? '' : ' (unavailable)'));
          var chk = document.createElement('span');
          chk.className = 'gma-cz__swatch-check';
          chk.textContent = '✓';
          chk.setAttribute('aria-hidden', 'true');
          b.appendChild(chk);
          b.addEventListener('click', function () {
            if (state.color !== color) { state.color = color; markProofStale(); applyChange('color'); }
          });
          els.colors.appendChild(b);
        });
      }
      if (els.colorName) els.colorName.textContent = state.color;

      /* sizes */
      if (els.sizes) {
        els.sizes.innerHTML = '';
        GMA_CONFIG.product.sizes.forEach(function (size) {
          var e = variantEntry(state.color, size);
          var usable = e && e.variantId != null && e.available !== false;
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'gma-cz__size' + (size === state.size ? ' is-active' : '');
          b.textContent = size;
          b.disabled = !usable;
          b.setAttribute('aria-pressed', size === state.size ? 'true' : 'false');
          b.setAttribute('aria-label', 'Size ' + size + (usable ? '' : ' (unavailable in ' + state.color + ')'));
          b.addEventListener('click', function () {
            if (state.size !== size) { state.size = size; markProofStale(); applyChange('size'); }
          });
          els.sizes.appendChild(b);
        });
      }
      if (els.sizeName) els.sizeName.textContent = state.size;

      /* view toggle */
      $$ (root, '[data-gma-view]').forEach(function (b) {
        var active = b.dataset.gmaView === state.view;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (els.productCard) els.productCard.classList.add('is-active');
    }

    function renderStage() {
      var tpl = GMA_CONFIG.templates.views[state.view];
      var region = tpl.regionPx;

      if (els.regionRect) {
        els.regionRect.setAttribute('x', region.x);
        els.regionRect.setAttribute('y', region.y);
        els.regionRect.setAttribute('width', region.w);
        els.regionRect.setAttribute('height', region.h);
        els.regionRect.hidden = !debug;
      }
      /* the clip path must follow the same region (front/back differ) */
      var clipRect = root.querySelector('[data-gma-region-clip]');
      if (clipRect) {
        clipRect.setAttribute('x', region.x);
        clipRect.setAttribute('y', region.y);
        clipRect.setAttribute('width', region.w);
        clipRect.setAttribute('height', region.h);
      }

      /* garment base for the selected color + view */
      var url = mockupUrl(state.view, state.color);
      if (els.baseImg) {
        if (url && runtime.loadedMockups[state.view + '|' + state.color]) {
          els.baseImg.setAttribute('href', url);
          els.baseImg.style.display = '';
        } else {
          els.baseImg.style.display = 'none';
        }
      }
      /* optional shading overlay (same render as the base; skipped if absent) */
      if (els.shadingImg) {
        var sh = (boot.mockups && boot.mockups.shading && boot.mockups.shading[state.view]) || '';
        if (sh) { els.shadingImg.setAttribute('href', sh); els.shadingImg.style.display = ''; }
        else { els.shadingImg.style.display = 'none'; }
      }

      var design = currentDesign();
      var geo = stageGeometry();
      if (els.artImg) {
        if (design && geo && !runtime.artBroken) {
          var src = designImageUrl(design);
          if (src) els.artImg.setAttribute('href', src);
          els.artImg.setAttribute('x', geo.x);
          els.artImg.setAttribute('y', geo.y);
          els.artImg.setAttribute('width', geo.w);
          els.artImg.setAttribute('height', geo.h);
          var p = state.placements[state.view];
          els.artImg.setAttribute('transform', p && p.rotationDegrees
            ? 'rotate(' + p.rotationDegrees + ' ' + geo.cx + ' ' + geo.cy + ')' : '');
          els.artImg.style.display = '';
        } else {
          els.artImg.style.display = 'none';
        }
      }

      if (els.stageMsg) {
        if (runtime.mockupError) {
          els.stageMsg.textContent = 'Preview unavailable right now — please try again in a moment.';
        } else if (!design) {
          els.stageMsg.textContent = 'Choose a design from the library to see it on the tee.';
        } else if (!runtime.artBroken) {
          els.stageMsg.textContent = '';
        }
      }
      if (els.printSize) {
        els.printSize.textContent = geo
          ? 'Prints at ' + geo.wIn.toFixed(1) + ' × ' + geo.hIn.toFixed(1) + ' in'
          : '';
      }
    }

    function formatMoney(cents) {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && boot.moneyFormat) {
        return window.Shopify.formatMoney(cents, boot.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    function renderPrice() {
      if (!els.price || !els.availability) return;
      if (state.availability === 'available' || state.availability === 'sold_out') {
        els.price.textContent = formatMoney(state.unitPriceCents);
      } else {
        els.price.textContent = '—';
      }
      var labels = {
        available: 'In stock — ships from our print partner',
        sold_out: 'Sold out in ' + state.color + ' / ' + state.size,
        unpriced: 'Unavailable until priced',
        unmapped: 'Unavailable until priced'
      };
      els.availability.textContent = labels[state.availability] || '';
    }

    /* Bottom color rail — same state, same handler path as the swatches
       (spec §3: never a second selection source). */
    function renderRail() {
      if (!els.rail) return;
      if (els.rail.parentElement) els.rail.parentElement.hidden = false;
      els.rail.innerHTML = '';
      GMA_CONFIG.product.colors.forEach(function (color) {
        var url = mockupUrl(state.view, color);
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'gma-cz__rail-item' + (color === state.color ? ' is-active' : '');
        b.setAttribute('aria-pressed', color === state.color ? 'true' : 'false');
        b.setAttribute('aria-label', 'Preview the tee in ' + color);
        if (url) {
          var img = document.createElement('img');
          img.src = url; img.alt = ''; img.loading = 'lazy';
          img.addEventListener('error', function () { img.remove(); });
          b.appendChild(img);
        }
        var span = document.createElement('span');
        span.textContent = color;
        b.appendChild(span);
        b.addEventListener('click', function () {
          if (state.color !== color) { state.color = color; markProofStale(); applyChange('rail-color'); }
        });
        els.rail.appendChild(b);
      });
    }

    /* ================= proof (spec §8) ================= */
    function configFingerprint() {
      return JSON.stringify({
        d: state.designId, v: state.variantId, c: state.color, s: state.size,
        p: state.placements
      });
    }

    function markProofStale() {
      if (state.proofStatus === 'valid' || state.proofStatus === 'pending') {
        state.proofStatus = 'stale';
      }
    }

    function proofPayload() {
      var design = currentDesign();
      return {
        design_id: state.designId,
        design_version: state.designVersion,
        design_title: design ? design.title : null,
        product_key: state.productKey,
        shopify_product_id: state.shopifyProductId,
        shopify_variant_id: state.variantId,
        color: state.color,
        size: state.size,
        placements: state.placements,   // per-view, region-normalized (spec §7)
        unit_price_cents: state.unitPriceCents,
        currency: 'USD'
      };
    }

    function scheduleProof() {
      if (!familyIsLive()) return;   // locked families never request proofs
      if (runtime.proofTimer) clearTimeout(runtime.proofTimer);
      runtime.proofTimer = setTimeout(validateProof, GMA_CONFIG.proofDebounceMs);
    }

    function validateProof() {
      if (!familyIsLive()) {
        state.proofStatus = 'idle';
        state.proofId = null;
        return Promise.resolve();
      }
      if (!state.designId || state.availability !== 'available' || runtime.artBroken) {
        state.proofStatus = state.designId ? state.proofStatus : 'idle';
        renderProofLine(); updateAtc();
        return Promise.resolve();
      }
      if (!boot.proofServiceUrl || !boot.turnstileSiteKey) {
        state.proofStatus = 'error';
        renderProofLine(); updateAtc();
        return Promise.resolve();
      }
      var seq = ++runtime.proofSeq;
      var fp = configFingerprint();
      state.proofStatus = 'pending';
      renderProofLine(); updateAtc();
      /* Turnstile: the bridge refuses any proof request without a fresh,
         single-use token (fail-closed), so one is fetched per request. */
      return getTurnstileToken()
        .then(function (token) {
          var payload = proofPayload();
          payload.turnstile_token = token;
          return fetch(boot.proofServiceUrl.replace(/\/$/, '') + '/proofs', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
          });
        })
        .then(function (r) {
          if (!r.ok) throw new Error('proof service ' + r.status);
          return r.json();
        })
        .then(function (data) {
          if (seq !== runtime.proofSeq) return;          // superseded
          if (fp !== configFingerprint()) {              // config moved on
            state.proofStatus = 'stale';
          } else {
            state.proofId = data.proof_id;
            state.proofStatus = 'valid';
          }
          renderProofLine(); updateAtc();
        })
        .catch(function () {
          if (seq !== runtime.proofSeq) return;
          state.proofStatus = 'error';
          state.proofId = null;
          renderProofLine(); updateAtc();
        });
    }

    /* ================= Cloudflare Turnstile ================= */
    /* One invisible widget per Studio. execute() mints a token; tokens are
       single-use, so the widget is reset before every new request. A newer
       request cancels an older pending one; 30 s without a token fails. */
    var ts = { widgetId: null, waiter: null };
    function loadTurnstile() {
      if (window.turnstile) return Promise.resolve(window.turnstile);
      if (window.__gmaTurnstileLoading) return window.__gmaTurnstileLoading;
      window.__gmaTurnstileLoading = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        s.async = true;
        s.onload = function () {
          if (window.turnstile) resolve(window.turnstile);
          else { window.__gmaTurnstileLoading = null; reject(new Error('turnstile missing')); }
        };
        s.onerror = function () { window.__gmaTurnstileLoading = null; reject(new Error('turnstile blocked')); };
        document.head.appendChild(s);
      });
      return window.__gmaTurnstileLoading;
    }
    function settleTurnstile(ok, value) {
      var w = ts.waiter;
      ts.waiter = null;
      if (!w) return;
      clearTimeout(w.timer);
      if (ok) w.resolve(value); else w.reject(new Error(value || 'turnstile failed'));
    }
    function getTurnstileToken() {
      if (!boot.turnstileSiteKey || !els.turnstile) return Promise.reject(new Error('turnstile not configured'));
      return loadTurnstile().then(function (t) {
        return new Promise(function (resolve, reject) {
          settleTurnstile(false, 'superseded');
          ts.waiter = { resolve: resolve, reject: reject,
            timer: setTimeout(function () { settleTurnstile(false, 'turnstile timeout'); }, 30000) };
          if (ts.widgetId === null) {
            ts.widgetId = t.render(els.turnstile, {
              sitekey: boot.turnstileSiteKey,
              execution: 'execute',
              appearance: 'interaction-only',
              callback: function (token) { settleTurnstile(true, token); },
              'error-callback': function () { settleTurnstile(false, 'turnstile error'); return true; },
              'expired-callback': function () { settleTurnstile(false, 'turnstile expired'); },
              'timeout-callback': function () { settleTurnstile(false, 'turnstile timeout'); }
            });
          } else {
            t.reset(ts.widgetId);
          }
          t.execute(ts.widgetId);
        });
      });
    }

    function renderProofLine() {
      if (!els.proofLine) return;
      var labels = {
        idle: '',
        pending: 'Preparing your print proof…',
        valid: 'Print proof saved — this exact design and placement goes to production.',
        stale: 'Proof will refresh for the new selection.',
        error: (boot.proofServiceUrl && boot.turnstileSiteKey)
          ? 'Proof service unreachable — ordering is paused. Please try again shortly.'
          : 'Custom ordering opens soon — proofs are not live yet.'
      };
      els.proofLine.textContent = labels[state.proofStatus] || '';
    }

    /* ================= Add to Cart gate + action ================= */
    function selectionPurchasable() {
      return familyIsLive() &&
        !!state.designId &&
        state.availability === 'available' &&
        typeof state.variantId === 'number' &&
        state.unitPriceCents != null &&
        !runtime.artBroken &&
        !runtime.mockupError &&
        !runtime.mockupLoading;
    }

    function updateAtc() {
      if (!els.atc) return;
      if (!familyIsLive()) {
        /* fail-closed: the button carries the family's registry state
           as its label and can never be enabled outside the tee */
        var fam = runtime.familiesByKey[state.productKey];
        els.atc.disabled = true;
        els.atc.textContent = familyStatusFor(fam).badge;
        return;
      }
      var label = 'Add to Cart';
      var enabled = false;
      if (runtime.adding) {
        label = 'Adding…';
      } else if (!state.designId) {
        label = 'Pick a design first';
      } else if (state.availability === 'unpriced' || state.availability === 'unmapped') {
        label = 'Unavailable until priced';
      } else if (state.availability === 'sold_out') {
        label = 'Sold out';
      } else if (runtime.mockupError) {
        label = 'Preview unavailable';
      } else if (runtime.artBroken) {
        label = 'Artwork unavailable';
      } else if (state.proofStatus === 'pending') {
        label = 'Preparing proof…';
      } else if (state.proofStatus === 'error') {
        label = 'Proof unavailable';
      } else if (selectionPurchasable() && state.proofStatus === 'valid') {
        enabled = true;
      } else if (selectionPurchasable()) {
        /* selection fine, proof not yet validated — clicking runs proof first */
        enabled = true;
      }
      els.atc.disabled = !enabled;
      els.atc.textContent = label;
    }

    function setStatus(msg, kind) {
      if (!els.status) return;
      els.status.textContent = msg || '';
      els.status.className = 'gma-cz__status' + (kind ? ' is-' + kind : '');
    }

    function onAddToCart() {
      if (runtime.adding || !selectionPurchasable()) return;
      runtime.adding = true;
      updateAtc();
      setStatus('Adding to cart…');

      var proceed = (state.proofStatus === 'valid' && state.proofId)
        ? Promise.resolve()
        : validateProof();

      proceed.then(function () {
        if (state.proofStatus !== 'valid' || !state.proofId) {
          throw new Error('We could not prepare a print proof. Please try again.');
        }
        var rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || boot.rootUrl || '/';
        var design = currentDesign();
        var usedViews = Object.keys(state.placements).filter(function (v) { return state.placements[v]; });
        var item = {
          id: Number(state.variantId),
          quantity: state.quantity,
          properties: {
            'Design': design ? design.title : '',
            'Product': GMA_CONFIG.product.name + ' — ' + state.color + ' / ' + state.size,
            'Print side': usedViews.map(titleCase).join(' + '),
            '_design_id': state.designId,
            '_design_version': state.designVersion || '1.0',
            '_proof_id': state.proofId,
            '_placement_json': JSON.stringify(state.placements)
          }
        };
        return fetch(rootPath + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [item] })
        }).then(function (res) {
          if (!res.ok) {
            return res.json().catch(function () { return null; }).then(function (e) {
              throw new Error((e && e.description) || 'Shopify rejected the cart add.');
            });
          }
          return res.json();
        }).then(function () { return rootPath; });
      }).then(function (rootPath) {
        setStatus('Added to cart — ' + (currentDesign() ? currentDesign().title : '') +
          ' on a ' + state.color + ' tee, size ' + state.size + '.', 'success');
        /* refresh any theme cart-count badges we can address generically */
        fetch(rootPath + 'cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            $$ (document, '[data-cart-count], .cart-count, .cart-count-bubble span').forEach(function (el) {
              el.textContent = cart.item_count;
            });
          })
          .catch(function () { /* theme-specific refresh left to the theme */ });
        document.dispatchEvent(new CustomEvent('gma:added-to-cart', {
          detail: { proofId: state.proofId, variantId: state.variantId }
        }));
      }).catch(function (err) {
        setStatus('Could not add to cart: ' + err.message, 'error');
      }).then(function () {
        runtime.adding = false;
        updateAtc();
      });
    }

    /* ================= events ================= */
    function bindEvents() {
      if (els.atc) els.atc.addEventListener('click', onAddToCart);
      if (els.clearDesign) els.clearDesign.addEventListener('click', clearDesign);

      if (els.search) {
        els.search.addEventListener('input', function () {
          runtime.filterQuery = els.search.value;
          runtime.visibleCount = GMA_CONFIG.pageSize;
          renderGrid();
        });
      }
      if (els.loadMore) {
        els.loadMore.addEventListener('click', function () {
          runtime.visibleCount += GMA_CONFIG.pageSize;
          renderGrid();           // selection survives paging (state-driven)
        });
      }
      if (els.libraryToggle && els.libraryBody) {
        els.libraryToggle.addEventListener('click', function () {
          var collapsed = els.libraryBody.classList.toggle('is-collapsed');
          els.libraryToggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        });
      }

      /* view switch — placements are kept per view, nothing is lost */
      $$ (root, '[data-gma-view]').forEach(function (b) {
        b.addEventListener('click', function () {
          var v = b.dataset.gmaView;
          if (v !== state.view && GMA_CONFIG.product.views.indexOf(v) !== -1) {
            state.view = v;
            markProofStale();
            applyChange('view');
          }
        });
      });

      /* zoom — camera only; never touches placement/print size (spec §3) */
      function setZoom(z) {
        runtime.zoom = clamp(z, 1, GMA_CONFIG.zoomMax);
        if (els.stageZoom) els.stageZoom.style.transform = 'scale(' + runtime.zoom + ')';
      }
      var zi = root.querySelector('[data-gma-zoom-in]');
      var zo = root.querySelector('[data-gma-zoom-out]');
      var zr = root.querySelector('[data-gma-zoom-reset]');
      if (zi) zi.addEventListener('click', function () { setZoom(runtime.zoom + GMA_CONFIG.zoomStep); });
      if (zo) zo.addEventListener('click', function () { setZoom(runtime.zoom - GMA_CONFIG.zoomStep); });
      if (zr) zr.addEventListener('click', function () { setZoom(1); });

      /* placement tools — bounded inside clampPlacement via applyChange */
      function withPlacement(fn) {
        var p = state.placements[state.view];
        if (!p || !currentDesign()) return;
        fn(p);
        markProofStale();
        applyChange('placement');
      }
      $$ (root, '[data-gma-tool]').forEach(function (b) {
        b.addEventListener('click', function () {
          var tool = b.dataset.gmaTool;
          withPlacement(function (p) {
            if (tool === 'center') { p.xNormalized = 0.5; p.yNormalized = 0.5; }
            if (tool === 'smaller') { p.scale = p.scale / GMA_CONFIG.scaleStep; }
            if (tool === 'larger') { p.scale = p.scale * GMA_CONFIG.scaleStep; }
            if (tool === 'reset') {
              var d = defaultPlacement();
              p.xNormalized = d.xNormalized; p.yNormalized = d.yNormalized; p.scale = d.scale; p.rotationDegrees = 0;
            }
          });
        });
      });
      $$ (root, '[data-gma-nudge]').forEach(function (b) {
        b.addEventListener('click', function () {
          var dir = b.dataset.gmaNudge;
          var step = GMA_CONFIG.nudgeStepNormalized;
          withPlacement(function (p) {
            if (dir === 'up') p.yNormalized -= step;
            if (dir === 'down') p.yNormalized += step;
            if (dir === 'left') p.xNormalized -= step;
            if (dir === 'right') p.xNormalized += step;
          });
        });
      });

      /* drag the artwork (pointer events; keyboard path = nudge buttons) */
      if (els.artImg && els.svg) {
        var drag = null;
        function toSvgPoint(evt) {
          var pt = els.svg.createSVGPoint();
          pt.x = evt.clientX; pt.y = evt.clientY;
          var ctm = els.svg.getScreenCTM();
          return ctm ? pt.matrixTransform(ctm.inverse()) : null;
        }
        els.artImg.addEventListener('pointerdown', function (evt) {
          var p = state.placements[state.view];
          if (!p) return;
          var sp = toSvgPoint(evt);
          if (!sp) return;
          drag = { id: evt.pointerId, startX: sp.x, startY: sp.y, x0: p.xNormalized, y0: p.yNormalized };
          els.artImg.setPointerCapture(evt.pointerId);
          evt.preventDefault();
        });
        els.artImg.addEventListener('pointermove', function (evt) {
          if (!drag || evt.pointerId !== drag.id) return;
          var sp = toSvgPoint(evt);
          if (!sp) return;
          var region = GMA_CONFIG.templates.views[state.view].regionPx;
          var p = state.placements[state.view];
          p.xNormalized = drag.x0 + (sp.x - drag.startX) / region.w;
          p.yNormalized = drag.y0 + (sp.y - drag.startY) / region.h;
          clampPlacement(p, currentDesign(), GMA_CONFIG.templates.views[state.view].regionIn);
          renderStage();   // live feedback; proof revalidates on release
        });
        ['pointerup', 'pointercancel'].forEach(function (ev) {
          els.artImg.addEventListener(ev, function () {
            if (drag) { drag = null; markProofStale(); applyChange('drag-end'); }
          });
        });
        /* arrow keys move the design when the stage has focus */
        var stageWrap = root.querySelector('[data-gma-stage-wrap]');
        if (stageWrap) {
          stageWrap.addEventListener('keydown', function (evt) {
            var step = GMA_CONFIG.nudgeStepNormalized;
            var used = true;
            withPlacement(function (p) {
              if (evt.key === 'ArrowUp') p.yNormalized -= step;
              else if (evt.key === 'ArrowDown') p.yNormalized += step;
              else if (evt.key === 'ArrowLeft') p.xNormalized -= step;
              else if (evt.key === 'ArrowRight') p.xNormalized += step;
              else used = false;
            });
            if (used) evt.preventDefault();
          });
        }
      }

      /* back to designs — scrolls to the library, draft preserved (spec §3) */
      var back = root.querySelector('[data-gma-back]');
      if (back) {
        back.addEventListener('click', function () {
          if (els.libraryBody) els.libraryBody.classList.remove('is-collapsed');
          var lib = root.querySelector('[data-gma-library]');
          if (lib && lib.scrollIntoView) lib.scrollIntoView({ behavior: 'smooth', block: 'start' });
          if (els.search) els.search.focus({ preventScroll: true });
        });
      }
    }

    /* ================= go ================= */
    bindEvents();
    renderProofLine();
    renderFamilies();   // empty until the registry asset resolves; loadFamilies re-renders
    loadFamilies();
    /* the blocklist must be known before the library renders */
    loadTradeRegistry().then(loadManifest);
  }

  document.addEventListener('DOMContentLoaded', function () {
    $$ (document, '[data-gma-customizer]').forEach(init);
  });
  /* theme editor re-render support */
  document.addEventListener('shopify:section:load', function (evt) {
    var root = evt.target.querySelector && evt.target.querySelector('[data-gma-customizer]');
    if (root) init(root);
  });
})();
